/**
 * CabinetTaxesPage — tuile Cabinet « Taxes TPS/TVQ » (Batch #14.257 · #14.280).
 * Perçues (ventes du cabinet) · CTI/RTI (achats du cabinet) · périodes de déclaration · corrections (pièces et sommaire) · rapports PDF/Excel/CSV.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarClock, Loader2, Percent } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { TaxSettingsCard } from "./cabinetTaxes/TaxSettingsCard";
import { PeriodsTable } from "./cabinetTaxes/PeriodsTable";
import { fmtDate, money, signedMoney } from "./cabinetTaxes/_shared";

const CUR_YEAR = new Date().getFullYear();
const YEARS = [CUR_YEAR + 1, CUR_YEAR, CUR_YEAR - 1, CUR_YEAR - 2, CUR_YEAR - 3];

function Kpi({ label, value, sub, tone = "slate", testId }) {
  const tones = {
    slate: "bg-white border-slate-200", indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900", rose: "bg-rose-50 border-rose-200 text-rose-900", amber: "bg-amber-50 border-amber-200 text-amber-900",
  };
  return (
    <div className={`border rounded-xl p-3 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function CabinetTaxesPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState(CUR_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/cabinet-taxes/overview", { params: { year } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chargement impossible");
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals;

  return (
    <div className="max-w-6xl mx-auto space-y-4" data-testid="cabinet-taxes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow"><Percent className="w-6 h-6" /></div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Taxes TPS/TVQ</h1>
            <p className="text-sm text-gray-500 mt-0.5">Taxes perçues sur tes ventes, CTI/RTI sur tes achats, déclarations par période.</p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-500 uppercase font-semibold">Exercice
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border rounded-lg px-3 py-2 text-sm normal-case" data-testid="cabinet-taxes-year-select">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
      </div>

      {data && <TaxSettingsCard settings={data.settings} onSaved={() => load()} />}

      {data?.drafts_total > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap bg-orange-50 border border-orange-200 rounded-xl p-3" data-testid="drafts-banner">
          <div className="flex items-start gap-2 text-sm text-orange-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span><strong>{data.drafts_total} pièce{data.drafts_total > 1 ? "s" : ""}</strong> non reportée{data.drafts_total > 1 ? "s" : ""} dans l'exercice : ses taxes ne sont pas comptées tant qu'elle n'est pas reportée.</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => navigate("/admin/comptabilite/transactions-clients?status=draft")} className="text-xs font-semibold text-orange-900 underline" data-testid="drafts-goto-sales">Ventes</button>
            <button onClick={() => navigate("/admin/comptabilite/transactions-fournisseurs?status=draft")} className="text-xs font-semibold text-orange-900 underline" data-testid="drafts-goto-purchases">Achats</button>
          </div>
        </div>
      )}

      {loading && !data ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi label="TPS perçue" value={money(t.tps_collected)} sub={`CTI ${money(t.cti)}`} testId="kpi-tps" />
            <Kpi label="TVQ perçue" value={money(t.tvq_collected)} sub={`RTI ${money(t.rti)}`} testId="kpi-tvq" />
            <Kpi label="Net de l'exercice" value={signedMoney(t.net_total)} sub={t.net_total < 0 ? "remboursement" : "à remettre au total"} tone="indigo" testId="kpi-net" />
            <Kpi label="Remis" value={money(t.remitted)} sub="périodes payées" tone="emerald" testId="kpi-remitted" />
            <Kpi label="Reste à remettre" value={signedMoney(t.net_unpaid)} sub="périodes terminées non payées" tone={t.net_unpaid > 0 ? "rose" : "slate"} testId="kpi-unpaid" />
            <Kpi label="Prochaine échéance" value={data.next_due ? fmtDate(data.next_due.due_date) : "—"}
              sub={data.next_due ? `${data.next_due.label.split(" (")[0]} · ${signedMoney(data.next_due.net_total)}` : "Tout est produit"}
              tone={data.overdue_count > 0 ? "rose" : "amber"} testId="kpi-next-due" />
          </div>

          {data.overdue_count > 0 && (
            <div className="flex items-center gap-2 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="overdue-banner">
              <CalendarClock className="w-4 h-4" /> <strong>{data.overdue_count} période(s)</strong> dont l'échéance est dépassée sans déclaration marquée produite.
            </div>
          )}

          <PeriodsTable periods={data.periods} onChanged={load} />

          <p className="text-[11px] text-slate-400">
            Méthode d'exercice : les taxes perçues suivent la date de la vente (notes de crédit en négatif) ; les CTI/RTI suivent la date de l'achat (notes de débit en négatif). Seules les pièces reportées comptent.
            En cas d'erreur : corrige la TPS / TVQ d'une pièce (crayon dans le détail) ou ajuste une ligne du sommaire (« Corriger les montants »).
            Les lignes 101 à 209 reprennent la numérotation du formulaire FPZ-500 pour faciliter la saisie dans Mon dossier (Revenu Québec).
          </p>
        </>
      )}
    </div>
  );
}
